/**
 * 操作ヘルプ（FR-A11Y-01 の「操作方法が分かること」）。
 *
 * 行はキーバインドの表（input.ts の KEY_HELP / TOUCH_HELP）から作る。キーを足せば
 * ヘルプも一緒に増えるため、実装と説明がずれない。文言はすべて textContent で入れる
 * （利用者辞書が HTML を持ち込んでも実行されない）。
 */
import { cls } from '../brand';
import type { Translate } from '../i18n/i18n';
import { KEY_HELP, TOUCH_HELP } from './input';
import type { PanelContent } from './panels';

/** キー名の表示形（記号は KeyboardEvent.key のまま出しても読めないものだけ直す）。 */
const KEY_LABELS: Record<string, string> = {
  ArrowRight: '→',
  ArrowLeft: '←',
  ArrowUp: '↑',
  ArrowDown: '↓',
  PageDown: 'Page Down',
  PageUp: 'Page Up',
  Escape: 'Esc',
};

export class Help implements PanelContent {
  readonly el: HTMLElement;

  constructor(doc: Document, translate: Translate) {
    this.el = doc.createElement('div');
    this.el.className = cls('help');

    const keys = KEY_HELP.map((row) => {
      const names = row.keys.map((key) => KEY_LABELS[key] ?? key).join(' / ');
      return {
        // 修飾キーは「Shift + ↑ / ↓ / ← / →」の形で頭に出す。
        term: row.modifier === undefined ? names : `${row.modifier} + ${names}`,
        description: translate(row.label),
      };
    });
    const touches = TOUCH_HELP.map((label) => ({ term: '', description: translate(label) }));

    this.el.append(
      this.section(doc, translate('help.keyboard'), keys),
      this.section(doc, translate('help.touch'), touches),
    );
  }

  dispose(): void {
    this.el.remove();
  }

  private section(
    doc: Document,
    title: string,
    rows: readonly { term: string; description: string }[],
  ): HTMLElement {
    const section = doc.createElement('section');
    section.className = cls('help-section');

    const heading = doc.createElement('h3');
    heading.className = cls('help-title');
    heading.textContent = title;

    const list = doc.createElement('dl');
    list.className = cls('help-list');
    for (const row of rows) {
      const term = doc.createElement('dt');
      term.className = cls('help-key');
      term.textContent = row.term;
      const description = doc.createElement('dd');
      description.className = cls('help-action');
      description.textContent = row.description;
      list.append(term, description);
    }

    section.append(heading, list);
    return section;
  }
}
